import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/utils/helpers";
import "./segmentedLens.css";

export interface KitSegmentedOption<T extends string = string> {
  label: string;
  value: T;
  /** Optional extra content after the label (a key hint in a tooltip, a count). */
  title?: string;
  /** Shown but not pickable yet (say why in `title`). */
  disabled?: boolean;
}

interface KitSegmentedControlProps<T extends string> {
  options: KitSegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  ariaLabel?: string;
  /**
   * Segmented control (CreativeOS Library.lib.pen, style B): a capsule track in
   * control glass and the chosen option under a capsule lens (--co-lens, one
   * soft shadow, no underline) that travels on the snappy spring (Settings
   * M05) and takes the chosen option's width. "lens" sizes
   * to its labels; "fill" spreads the options across the width.
   */
  variant?: "fill" | "lens";
  /** Lens only: the height of the track, 36 by default, 44 for touch. */
  size?: "default" | "large";
  /** Lens only: rendered inside each option after its label. */
  renderOption?: (option: KitSegmentedOption<T>, selected: boolean) => ReactNode;
}

export function KitSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  ariaLabel = "Toggle",
  variant = "fill",
  size = "default",
  renderOption,
}: KitSegmentedControlProps<T>) {
  return (
    <LensSegmentedControl
      options={options}
      value={value}
      onChange={onChange}
      className={className}
      ariaLabel={ariaLabel}
      size={size}
      renderOption={renderOption}
      spread={variant === "fill"}
    />
  );
}

function LensSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  ariaLabel,
  size,
  renderOption,
  spread = false,
}: {
  options: KitSegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  ariaLabel: string;
  size: "default" | "large";
  renderOption?: (option: KitSegmentedOption<T>, selected: boolean) => ReactNode;
  /** "fill": the options share the width equally; the lens still travels. */
  spread?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const [lens, setLens] = useState<{ x: number; width: number } | null>(null);
  const [pressed, setPressed] = useState(false);
  // Reduced motion swaps the slide for a 160ms cross-fade: the lens remounts on
  // each choice so its fade-in runs, instead of travelling.
  const [reduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  // The lens is measured from the chosen option, so labels of any length work.
  useLayoutEffect(() => {
    const measure = () => {
      const node = buttons.current.get(value);
      // A value none of the options carries (a pick made beside the control)
      // leaves the track without a lens.
      if (!node) {
        setLens(null);
        return;
      }
      setLens((previous) =>
        previous && previous.x === node.offsetLeft && previous.width === node.offsetWidth
          ? previous
          : { x: node.offsetLeft, width: node.offsetWidth },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (trackRef.current) observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [value, options]);

  const index = Math.max(0, options.findIndex((option) => option.value === value));
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    const next = options[(index + step + options.length) % options.length];
    if (next.disabled) return;
    onChange(next.value);
    buttons.current.get(next.value)?.focus();
  };

  return (
    <div
      ref={trackRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn(
        "co-glass-control relative items-stretch rounded-pill p-[3px]",
        spread ? "grid" : cn("inline-flex", size === "large" ? "h-11" : "h-9"),
        className,
      )}
      style={spread ? { gridTemplateColumns: `repeat(${options.length}, 1fr)` } : undefined}
    >
      {lens ? (
        <span
          aria-hidden="true"
          key={reduced ? value : "lens"}
          data-pressed={pressed}
          className="co-seg-lens pointer-events-none absolute bottom-[3px] left-0 top-[3px]"
          style={{ width: lens.width, transform: `translateX(${lens.x}px)` }}
        />
      ) : null}
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) buttons.current.set(option.value, node);
              else buttons.current.delete(option.value);
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            title={option.title}
            disabled={option.disabled}
            onPointerDown={() => selected && setPressed(true)}
            onPointerUp={() => setPressed(false)}
            onPointerLeave={() => setPressed(false)}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative z-[1] inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-3 text-[13px] outline-none",
              spread && "justify-center py-1.5",
              /* Settings M05: the labels swap weight and colour in a 160ms cross-fade. */
              "transition-[color,font-weight] duration-[var(--duration-fast)] ease-out",
              "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
              selected ? "font-semibold text-co-text" : "font-medium text-co-muted hover:text-co-text",
              "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-co-muted",
            )}
          >
            {option.label}
            {renderOption?.(option, selected)}
          </button>
        );
      })}
    </div>
  );
}
